/**
 * Admin-only API client for the 10DLC messaging-number pool.
 *
 * Endpoints live under `/admin/messaging/numbers/*` (no `/api` prefix
 * — this matches the `data_changes` admin convention).
 *
 * Tenant scope and ADMIN-role gating are enforced server-side by the
 * JWT middleware. UI should still hide the entry point for non-admins,
 * but the API contract here trusts the backend to refuse if needed.
 */
import client from '@/api/client';
import type {
  MessagingNumber,
  MessagingNumberSearchResult,
  BuyMessagingNumberRequest,
  UpdateMessagingNumberRequest,
} from '@/types/messaging';

/**
 * GET /admin/messaging/numbers
 *
 * Returns every number in the tenant's pool — active, paused, draining,
 * and released. Filter client-side if needed; the pool is small.
 */
export async function listMessagingNumbers(): Promise<MessagingNumber[]> {
  const res = await client.get<MessagingNumber[]>('/admin/messaging/numbers');
  return res.data;
}

/**
 * GET /admin/messaging/numbers/search
 *
 * Proxies Twilio's available-numbers search. `area_code` is required;
 * `contains` is an optional digit/letter pattern.
 */
export async function searchAvailableNumbers(opts: {
  area_code: string;
  contains?: string;
  limit?: number;
}): Promise<MessagingNumberSearchResult[]> {
  const params: Record<string, string | number> = {
    area_code: opts.area_code,
  };
  if (opts.contains) params.contains = opts.contains;
  if (opts.limit !== undefined) params.limit = opts.limit;
  const res = await client.get<MessagingNumberSearchResult[]>(
    '/admin/messaging/numbers/search',
    { params },
  );
  return res.data;
}

/**
 * POST /admin/messaging/numbers
 *
 * Purchases a number from the provider and adds it to the pool. The
 * server side-effect is real (charges money), so the UI should confirm
 * before calling.
 */
export async function buyMessagingNumber(
  payload: BuyMessagingNumberRequest,
): Promise<MessagingNumber> {
  const res = await client.post<MessagingNumber>(
    '/admin/messaging/numbers',
    payload,
  );
  return res.data;
}

/**
 * PATCH /admin/messaging/numbers/{id}
 *
 * Updates editable fields (status, notes). Status transitions are
 * validated server-side.
 */
export async function updateMessagingNumber(
  id: number,
  payload: UpdateMessagingNumberRequest,
): Promise<MessagingNumber> {
  const res = await client.patch<MessagingNumber>(
    `/admin/messaging/numbers/${id}`,
    payload,
  );
  return res.data;
}

/**
 * DELETE /admin/messaging/numbers/{id}?confirm=true
 *
 * Releases the number back to the provider. Server REQUIRES the
 * `confirm=true` query param to avoid accidental releases.
 */
export async function releaseMessagingNumber(id: number): Promise<void> {
  await client.delete(`/admin/messaging/numbers/${id}`, {
    params: { confirm: true },
  });
}
